import './EmployeeTable.scss'
import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { IUserResponse, IUserEntityAssignment } from '@getgreenline/homi-shared'
import { Link, browserHistory } from 'react-router'
import { Routes } from '../../../constants/Routes'
import { Tooltip, Table, Tag, Icon, Dropdown, Menu, Button } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import Highlighter from 'react-highlight-words'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { EmployeeFilterStore } from './EmployeeFilterStore'
import { ADMINISTRATOR } from './AddEmployee'
import { isUndefined } from 'lodash'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  filter: EmployeeFilterStore
  toggleDeleteModal: (user?: IUserResponse) => void
}

interface EmployeeTableRecord {
  name: JSX.Element
  email: JSX.Element
  passcode: JSX.Element
  loactionsAndRoles: JSX.Element
  options: JSX.Element | false
}

const isAssignedToCompany = (ea: IUserEntityAssignment, companyId: number) =>
  ea.entity.rootEntityId === companyId || ea.entity.parentEntityId === companyId

const columns: ColumnProps<EmployeeTableRecord>[] = [
  { title: 'Name', dataIndex: 'name', key: 'name' },
  {
    title: (
      <>
        <span className='mr-2'>Email</span>
        <Tooltip
          title={
            <span>
              Used to log into the dashboard.
              <br />
              For POS access, use the passcode.
            </span>
          }
        >
          <Icon type='question-circle' />
        </Tooltip>
      </>
    ),
    dataIndex: 'email',
    key: 'email',
  },
  {
    title: (
      <>
        <span className='mr-2'>POS Passcode</span>
        <Tooltip title='Used to sign into the POS.'>
          <Icon type='question-circle' />
        </Tooltip>
      </>
    ),
    dataIndex: 'passcode',
    key: 'passcode',
  },
  {
    title: (
      <>
        <span className='mr-2'>Locations/Roles</span>
        <Tooltip title='Employees can be assigned to multiple locations with different security roles.'>
          <Icon type='question-circle' />
        </Tooltip>
      </>
    ),
    dataIndex: 'loactionsAndRoles',
    key: 'loactionsAndRoles',
  },
  { title: 'Options', dataIndex: 'options', key: 'options', width: 92, align: 'center' },
]

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class EmployeeTable extends Component<Props> {
  company = this.props.currentCompanyStore!.company!

  renderLocationAndRoles(entities: IUserEntityAssignment[]): JSX.Element {
    const locations = this.props.filter.locations
    if (locations.length === 0) return <>-</>

    const assignedLocations = entities.filter(
      (entity) => entity.type === 'assigned' && entity.entity.rootEntityId === this.company.id,
    )
    const isAllLocationsAccessible = assignedLocations.find(
      (al) => al.entity.id === al.entity.rootEntityId,
    )

    return (
      <>
        {assignedLocations.length === 0
          ? '-'
          : assignedLocations.map((location, index) => (
              <p key={`tag_${location.securityRoleId}${index}`}>
                <span className='mr-2'>
                  {isAllLocationsAccessible ? 'All locations' : location.entity.name}
                </span>
                {this.renderSecurityRoleTag(location.securityRoleId)}
              </p>
            ))}
      </>
    )
  }

  renderSecurityRoleTag(roleId: number | null): JSX.Element | undefined {
    const securityRoles = this.props.filter.securityRoles
    const role = securityRoles.find((securityRole) => securityRole.id === roleId)

    if (!this.company || !role) return

    return (
      <Link className='gr-link' to={Routes.DASHBOARD_EMPLOYEES_SECURITY_ROLES(this.company.id)}>
        <Tag color={role.name === 'Administrator' ? 'green' : undefined}>{role.name}</Tag>
      </Link>
    )
  }

  splitSearchWords(words?: string): string[] {
    if (!words) return []
    return [...words.split(' ')]
  }

  checkIfCurrentUserCanManageEmployee = (employee: IUserResponse) => {
    if (this.props.currentUserStore?.isAdmin) return true

    const employeeEntityAssignmentIds = employee.entityAssignments
      .filter((ea) => isAssignedToCompany(ea, this.company.id))
      .map((entityAssignment) => entityAssignment.entity.id)

    const administratorId = this.props.currentCompanyStore?.securityRoles?.find(
      (securityRole) => securityRole.name === ADMINISTRATOR,
    )?.id

    const companyAssignment = this.props.currentUserStore?.currentUser?.entityAssignments.find(
      (entityAssignment) => entityAssignment.entity.entityType === 'company',
    )

    if (!isUndefined(administratorId) && companyAssignment?.securityRoleId === administratorId) {
      return true
    }

    const currentUserEntityAssignmentIds = new Set(this.props.currentUserStore?.entityAssignmentIds)

    if (
      !employeeEntityAssignmentIds.every((entityId) => currentUserEntityAssignmentIds.has(entityId))
    ) {
      return false
    }

    return true
  }

  isCognitoUser = (user: IUserResponse) => {
    return !!user.cognitoUserId
  }

  render() {
    const { toggleDeleteModal, filter } = this.props
    const { users, isFiltered, filteredUsers, searchText } = filter

    const employeeData = isFiltered ? filteredUsers : users

    const tableDataSource: EmployeeTableRecord[] = employeeData.map((employee: IUserResponse) => {
      const { id, name, email, hasPassword, registerPasscode, entityAssignments } = employee

      return {
        key: id,
        name: this.checkIfCurrentUserCanManageEmployee(employee) ? (
          <Link to={Routes.DASHBOARD_EDIT_EMPLOYEE(this.company.id, id)}>
            <Highlighter
              highlightClassName='highlighted-text'
              searchWords={this.splitSearchWords(isFiltered ? searchText : '')}
              autoEscape
              textToHighlight={name}
            />
          </Link>
        ) : (
          <Tooltip
            title='This employee belongs to locations you are not assigned to'
            placement='right'
          >
            <Highlighter
              highlightClassName='highlighted-text'
              searchWords={this.splitSearchWords(isFiltered ? searchText : '')}
              autoEscape
              textToHighlight={name}
            />
          </Tooltip>
        ),
        email: (
          <div className='employee-cell'>
            <span className='mr-2'>
              <Highlighter
                highlightClassName='highlighted-text'
                searchWords={this.splitSearchWords(isFiltered ? searchText : '')}
                autoEscape
                textToHighlight={email || '-'}
              />
            </span>
            {!hasPassword && !this.isCognitoUser(employee) && (
              <Tooltip
                title={
                  <>
                    No password set for this employee.
                    <br />
                    Will not be able to log into the dashboard.
                  </>
                }
              >
                <Icon type='exclamation-circle' style={{ color: 'var(--yellow)' }} />
              </Tooltip>
            )}
          </div>
        ),
        passcode: (
          <Highlighter
            highlightClassName='highlighted-text'
            searchWords={this.splitSearchWords(isFiltered ? searchText : '')}
            autoEscape
            textToHighlight={registerPasscode || '-'}
          />
        ),
        loactionsAndRoles: (
          <div className='employee-cell'>{this.renderLocationAndRoles(entityAssignments)}</div>
        ),
        options: this.checkIfCurrentUserCanManageEmployee(employee) && (
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu>
                <Menu.Item
                  key='edit'
                  onClick={() =>
                    browserHistory.push(Routes.DASHBOARD_EDIT_EMPLOYEE(this.company.id, id))
                  }
                >
                  <span style={{ color: 'var(--blue)' }}>Edit</span>
                </Menu.Item>

                <Menu.Item
                  key='delete'
                  onClick={() => {
                    toggleDeleteModal(employee)
                  }}
                >
                  <span style={{ color: 'var(--red)' }}>Delete</span>
                </Menu.Item>
              </Menu>
            }
          >
            <Button icon='ellipsis' style={{ transform: 'rotate(90deg)' }} />
          </Dropdown>
        ),
      }
    })

    return !users ? (
      <div>
        <h3 className='text-muted'>You have not added any employees yet</h3>
      </div>
    ) : (
      <Table
        columns={columns}
        dataSource={tableDataSource}
        pagination={false}
        scroll={{ x: 'max-content' }}
        className='employee-table no-background-table intercom-pb-3'
        rowClassName={() => 'background-white'}
      />
    )
  }
}
